const sanitizeHtml = require('sanitize-html');
const format = require('string-format');

const errors = require('./errors');
const validationMessages = require('./messages/validation');
const codeUtil = require('./text/code-util');

function internalSanitizeText(text) {
  return sanitizeHtml(text, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(['h1', 'h2', 'img', 'sub', 'sup', 's', 'u']),
    allowedAttributes: {
      span: ['style', 'class'],
      p: ['style', 'class'],
      img: ['src', 'style', 'class', 'alt'],
      a: ['href', 'target'],
      table: ['style', 'class'],
      th: ['style', 'class', 'colspan', 'rowspan'],
      td: ['style', 'class', 'colspan', 'rowspan'],
      ol: ['style'],
      ul: ['style'],
    },
    allowedSchemesByTag: { img: ['http'] },
  });
}

function internalSanitizeHTML(html) {
  return sanitizeHtml(html, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(['h1', 'h2', 'img', 'sub', 'sup', 's', 'u']),
    allowedAttributes: {
      span: ['style', 'class'],
      p: ['style', 'class'],
      img: ['src', 'style', 'class', 'alt'],
      a: ['href', 'target'],
      table: ['style', 'class'],
      th: ['style', 'class', 'colspan', 'rowspan'],
      td: ['style', 'class', 'colspan', 'rowspan'],
      ol: ['style'],
      ul: ['style'],
    },
    allowedSchemesByTag: { img: ['data', 'http'] },
  });
}

function manualStrip(t) {
  if (!t) {
    return '';
  }
  return t.replace(/(<\/?[A-Za-z]\w*([^>]*)>)|[\n\r\t]/g, '');
}

/* eslint-disable max-len */
/* eslint-disable no-useless-escape */
// Valid email regex retrieved on Feb 26, 2021 from
// https://emailregex.com/ (allows dashes before and after @ & .)
const validEmailFormat = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
// https://www.w3resource.com/javascript/form/email-validation.php (doesn't require both domain & tld)
// const validEmailFormat = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
// https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address (doesn't require both domain & tld)
// const validEmailFormat = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
// https://stackoverflow.com/a/201378/14423183 (doesn't allow dashes in domain name)
// const validEmailFormat = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;
/* eslint-enable no-useless-escape */
/* eslint-enable max-len */

module.exports = {
  assertSanitizedText(text, fieldName, objectId) {
    const sanitizedText = internalSanitizeText(text);

    if (sanitizedText !== text) {
      throw new errors.ValidationError(fieldName, objectId, format(validationMessages.string.fieldXFailedSantization, fieldName));
    }
  },

  assertSanitizedHTML(html, fieldName, objectId) {
    if (html) {
      const sanitizedHTML = internalSanitizeHTML(html);
      if (sanitizedHTML.replace(/ /g, '') !== html.replace(/ /g, '')) {
        throw new errors.ValidationError(fieldName, objectId, format(validationMessages.string.fieldXFailedSantization, fieldName));
      }
    }
  },

  assertTextIsDisplayable(text, fieldName, objectId) {
    if (!text || text.trim().length === 0) {
      throw new errors.ValidationError(fieldName, objectId, format(validationMessages.string.fieldXIsEmpty, fieldName));
    }
  },

  escape: str => str.replace(/[&<>'"]/g,
    tag => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
    }[tag])),

  sanitizeHTML: internalSanitizeHTML,
  stripHTMLTags: html => sanitizeHtml(html, {
    allowedTags: [],
    allowedAttributes: [],
  }),
  manualStrip,
  stripTags: manualStrip,
  cleanData: value => manualStrip(value).trim(),
  cleanSearchData(str) {
    if (!str) return str;
    let s = str.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;');
    s = s.replace(/\+/g, '');
    s = s.replace(/\(/g, '');
    s = s.replace(/\\/g, '');
    s = s.replace(/\//g, '');
    s = s.replace(/\*/g, '');
    s = s.replace(/\)/g, '');
    s = s.replace(/\[/g, '');
    s = s.replace(/\?/g, '');
    s = s.replace(/\]/g, '');
    return s;
  },

  isEmail(potentialEmail) {
    return validEmailFormat.test(potentialEmail);
  },

  format,
  ...codeUtil,
};
